import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { AssetTypeForm } from './AssetTypeForm';

import { useCreateAssetTypeMutation } from '~/modules/transportation/hooks/mutations/useCreateAssetTypeMutation';
import {
  $assetTypeForm,
  $updateAssetTypeForm,
} from '~/modules/transportation/state/assetTypeForm.localState';
import { AssetTypeFormState } from '~/modules/transportation/types';

type AssetTypeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  assetType?: AssetTypeFormState;
};

export const AssetTypeDialog = ({
  isOpen,
  onClose,
  onSuccess,
  assetType,
}: AssetTypeDialogProps) => {
  const toast = useToast();
  const t = useTranslations();
  const form = useAtomValue($assetTypeForm);
  const updateForm = useSetAtom($updateAssetTypeForm);
  const { mutateAsync: createAssetType } = useCreateAssetTypeMutation();

  React.useEffect(() => {
    if (assetType) {
      updateForm({
        name: assetType.name,
        description: assetType.description,
        capacity: assetType.capacity,
      });
    }
  }, [assetType, updateForm]);

  const handleClose = () => {
    onClose();
    // Reset form when closing
    updateForm({
      name: '',
      description: '',
      capacity: 0,
    });
  };

  const handleSave = async () => {
    try {
      if (assetType) {
        // TODO: Implement update mutation
        toast.error(t.assetTypesView.form.messages.updateNotImplemented);
      } else {
        await createAssetType({
          name: form.name,
          description: form.description,
          capacity: form.capacity,
        });
        toast.success(t.assetTypesView.form.messages.createSuccess);
      }
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.assetTypesView.form.messages.error,
      );
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@AssetTypeDialog"
    >
      <DialogContent slot="title" position="center">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {assetType
              ? t.assetTypesView.form.title.edit
              : t.assetTypesView.form.title.create}
          </div>
        </DialogHeader>

        <div className="p-4">
          <AssetTypeForm isEditing={!!assetType} />
        </div>

        <DialogFooter>
          <Button onPress={handleClose}>
            {t.assetTypesView.form.buttons.cancel}
          </Button>
          <Button color="primary" variant="solid" onPress={handleSave}>
            {assetType
              ? t.assetTypesView.form.buttons.save
              : t.assetTypesView.form.buttons.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
